import { type Control, Controller, type FieldErrors, type UseFormRegister, useWatch } from 'react-hook-form'

import { ButtonGroup, YesNo } from '@/components/ui/button-group'
import { CardCheckbox } from '@/components/ui/CardCheckbox'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { ClaimInjuredParty } from '../../../../models/claim'
import type { Step4FormValues } from './claimStep4Values'

const ANY_INJURIES_OPTIONS: { value: boolean; label: string }[] = [
  { value: true, label: 'Yes — injuries reported' },
  { value: false, label: 'No injuries' },
]

export const INJURED_PARTY_OPTIONS: { value: ClaimInjuredParty; label: string }[] = [
  { value: ClaimInjuredParty.OUR_DRIVER, label: 'Our Driver' },
  { value: ClaimInjuredParty.OUR_PASSENGER, label: 'Our Passenger' },
  { value: ClaimInjuredParty.OTHER_DRIVER, label: 'Other Driver' },
  { value: ClaimInjuredParty.OTHER_PASSENGER, label: 'Other Passenger' },
  { value: ClaimInjuredParty.PEDESTRIAN_CYCLIST, label: 'Pedestrian / Cyclist' },
]

interface ClaimInjuriesSectionProps {
  control: Control<Step4FormValues>
  register: UseFormRegister<Step4FormValues>
  errors: FieldErrors<Step4FormValues>
}

export function ClaimInjuriesSection({ control, register, errors }: ClaimInjuriesSectionProps) {
  const anyInjuries = useWatch({ control, name: 'anyInjuries' })

  return (
    <>
      <Controller
        name="anyInjuries"
        control={control}
        rules={{ validate: (v) => v !== null || 'Please choose.' }}
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-neutral-700">Were there any injuries? *</label>
            <ButtonGroup options={ANY_INJURIES_OPTIONS} value={field.value} onChange={field.onChange} />
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />

      {anyInjuries === true && (
        <div className="grid gap-5 sm:grid-cols-2">
          <Controller
            name="injuredParties"
            control={control}
            rules={{ validate: (v, f) => f.anyInjuries !== true || v.length > 0 || 'Select at least one.' }}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-neutral-700">Who was injured? *</label>
                <div className="flex flex-col gap-2">
                  {INJURED_PARTY_OPTIONS.map((o) => (
                    <CardCheckbox
                      key={o.value}
                      isChecked={field.value.includes(o.value)}
                      onChange={(checked) =>
                        field.onChange(
                          checked ? [...field.value, o.value] : field.value.filter((x) => x !== o.value)
                        )
                      }
                    >
                      {o.label}
                    </CardCheckbox>
                  ))}
                </div>
                <FieldError message={fieldState.error?.message} />
              </div>
            )}
          />

          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-neutral-700">Number of people injured *</label>
              <Input
                type="number"
                min={0}
                inputMode="numeric"
                aria-invalid={errors.numInjured ? true : undefined}
                {...register('numInjured', {
                  validate: (val, f) => f.anyInjuries !== true || val.trim() !== '' || 'Required.',
                })}
              />
              <FieldError message={errors.numInjured?.message} />
            </div>
            <Controller
              name="transportedToHospital"
              control={control}
              rules={{ validate: (v, f) => f.anyInjuries !== true || v !== null || 'Please choose.' }}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-neutral-700">
                    Was anyone transported to hospital? *
                  </label>
                  <YesNo value={field.value} onChange={field.onChange} />
                  <FieldError message={fieldState.error?.message} />
                </div>
              )}
            />
          </div>
        </div>
      )}
    </>
  )
}
